import { PAUSED_UPLOAD_TTL_HOURS } from "@/components/UploadProgress/UploadProgressLoadingCard";
import { ArticleBody, ArticleCallout, ArticleTable, ArticleTableWrap } from "./Article.elements";

const ROWS = [
  ["Uploading", "Bytes are flowing at whatever speed your connection allows.", "Nothing. Leave the tab open."],
  [
    "Queued",
    "This file waits while a few others upload. Files go a couple at a time so each one finishes sooner.",
    "Nothing.",
  ],
  [
    "Upload speed is slower than expected",
    "Weak signal. The uploaded counter switches to KB so you can see it move.",
    "Nothing — or move closer to the router if you can.",
  ],
  [
    "Connection stalled — retrying automatically… / Reconnecting…",
    "No bytes for a while. The uploader is already retrying and will continue from the last confirmed chunk.",
    "Wait. Do not cancel — cancelling throws away what is already uploaded.",
  ],
  [
    "You appear to be offline",
    "The browser reports no network at all.",
    "Reconnect or move. The upload continues by itself when the network is back.",
  ],
  [
    "Retrying (per file)",
    "That file hit an error and is being sent again from its last confirmed chunk.",
    "Nothing.",
  ],
  [
    "Upload paused (auto)",
    "The connection stayed bad long enough that the app paused the upload to protect it. It tries to resume itself; if that keeps failing you get one small \"Resume?\" popup.",
    `Press the play icon when you have signal — any time within ${PAUSED_UPLOAD_TTL_HOURS} hours.`,
  ],
  [
    "Paused (by you)",
    "You pressed the pause icon. The card shows how long the paused upload is kept.",
    `Press play within ${PAUSED_UPLOAD_TTL_HOURS} hours. Nothing already uploaded is re-sent.`,
  ],
  [
    "Upload interrupted by page reload / Select files",
    "The page was reloaded or the tab closed. Browsers don't let a page keep files across a reload.",
    "Select the same files or folder once more; already-uploaded chunks are skipped.",
  ],
  [
    "Uploading · another device / Paused · another device",
    "This package was started from another computer or browser.",
    "Continue there, or press Take over here to carry on from this device.",
  ],
  [
    "Files uploaded. Finishing package preparation…",
    "All bytes are on the server; it is assembling and verifying the files.",
    "Nothing. You can close the tab.",
  ],
  ["Done", "Delivered. The package appears under Uploads.", "—"],
  [
    "Failed",
    "The server rejected the package (rare: unsupported file, server error).",
    "Press Retry on the card. If it fails again, copy the support snapshot from the footer and contact support.",
  ],
];

export const UploadStatusesArticle = () => (
  <ArticleBody>
    <p>
      Every state the upload card can show, what it means, and what — if anything — you should do. The short
      version: <strong>waiting is almost always the right move</strong>; the uploader recovers on its own.
    </p>
    <ArticleTableWrap>
      <ArticleTable>
        <thead>
          <tr>
            <th>You see</th>
            <th>What it means</th>
            <th>What to do</th>
          </tr>
        </thead>
        <tbody>
          {ROWS.map(([label, meaning, action]) => (
            <tr key={label}>
              <td>{label}</td>
              <td>{meaning}</td>
              <td>{action}</td>
            </tr>
          ))}
        </tbody>
      </ArticleTable>
    </ArticleTableWrap>
    <ArticleCallout>
      Use the state switcher above the card on this page to see each of these live and try the pause / play icon.
    </ArticleCallout>
  </ArticleBody>
);
